/**
 * Validates a YouTube URL and extracts the video ID
 * @param url - The YouTube URL to validate
 * @returns The video ID if valid, empty string otherwise
 */
export const validateYouTubeUrl = (url: string): boolean => {
  if (!url) return false;

  // Regular expressions for different YouTube URL formats
  const patterns = [
    // Standard YouTube URL
    /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})$/,
    // Short YouTube URL
    /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/)([a-zA-Z0-9_-]{11})$/,
    // YouTube embed URL
    /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})$/,
    // YouTube Shorts URL (optional trailing query params)
    /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})(?:\?.*)?$/,
    // YouTube URL with additional parameters
    /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})(?:&.*)?$/,
  ];

  return patterns.some((pattern) => pattern.test(url));
};

export const extractYouTubeVideoId = (url: string): string | null => {
  if (!url) return null;

  // Regular expression to match YouTube video ID
  const regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  if (!match || !match[2]) return null;
  return match[2].length === 11 ? match[2] : null;
};

/**
 * Converts a YouTube URL to an embed URL
 * Supports various YouTube URL formats:
 * - Full watch URL: https://www.youtube.com/watch?v=VIDEO_ID
 * - Short URL: https://youtu.be/VIDEO_ID
 * - Embed URL: https://www.youtube.com/embed/VIDEO_ID
 * - Shorts URL: https://www.youtube.com/shorts/VIDEO_ID
 * - Just the video ID: VIDEO_ID
 *
 * @param url - The YouTube URL to convert
 * @param autoplay - Whether to autoplay the video (default: false)
 * @param mute - Whether to start muted (default: true, required for autoplay)
 */
export const getYouTubeEmbedUrl = (
  url: string,
  autoplay = false,
  mute = true
): string => {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return "";

  // Construct embed URL with parameters
  const params = new URLSearchParams();
  if (autoplay) params.append("autoplay", "1");
  if (mute) params.append("mute", "1");
  params.append("rel", "0"); // Don't show related videos
  params.append("modestbranding", "0"); // Minimal YouTube branding

  return `https://www.youtube.com/embed/${videoId}${
    params.toString() ? `?${params.toString()}` : ""
  }`;
};
